import {
  useDeletePayrollBatchMutation,
  useGetAllPayrollBatchQuery,
  type PayrollBatchItem,
} from '@/app/features/employee/emp-payroll';
import ConfirmationModal from '@/components/ConfirmationModal';
import CustomTable from '@/components/CustomTable';
import StatusTag from '@/components/StatusTag';
import { DISCLOSER_KEYS, FILTER_KEYS } from '@/constants/keys.constants';
import { useAppDiscloser, useAppFilters } from '@/hooks';
import { EyeIcon, TrashIcon } from '@/icons';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { formatDate, normalizeColumnValue } from '@/utils';
import { formatBatchPeriod } from '@/utils/payroll-cycle.utils';
import { Button, Space, Tooltip, type TableProps } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router';
import ManagePayroll from './_components/PayrollGenerate/ManagePayroll';
import { canDeleteBatch } from './_components/PayrollGenerate/batch-payroll.helpers';
import {
  PAY_BASIS_CONFIG,
  PAYMENT_STATUS_CONFIG,
} from './_components/shared/payroll.constants';
import { formatBDT } from './_components/shared/payroll.helpers';

/** "3 / 5" sheets generated; teal once every employee has one. */
const GeneratedCount = ({ batch }: { batch: PayrollBatchItem }) => {
  const done = batch.generated_salary_sheets ?? 0;
  const total = batch.total_employees ?? 0;
  const isComplete = total > 0 && done >= total;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-semibold ${
        isComplete
          ? 'bg-theme-primary/15 text-theme-primary'
          : 'bg-theme-yellow-500/15 text-theme-yellow-500'
      }`}
    >
      <span>{done}</span>
      <span className="opacity-40">/</span>
      <span className="opacity-70">{total}</span>
    </span>
  );
};

/**
 * Payroll & Salary Sheet Generate: every payroll batch. A batch opens into its
 * employee rows, and once its salary sheet is generated, into its bank list.
 * The toolbar (search, filters, Generate Salary) lives in `Filters`.
 */
export default function PayrollGenerate() {
  const navigate = useNavigate();

  const [config, handleOpen, handleClose] = useAppDiscloser<
    never,
    PayrollBatchItem
  >(DISCLOSER_KEYS.PAYROLL_SALARY_SHEET);
  const { page, limit, filters, keyword, handlePagination } = useAppFilters(
    FILTER_KEYS.PAYROLL_SALARY_SHEET,
  );

  const { data, isLoading, isFetching } = useGetAllPayrollBatchQuery({
    params: { page, limit, search: keyword, ...filters },
  });

  const [deletePayrollBatch, { isLoading: deleting }] =
    useDeletePayrollBatchMutation();

  const handleDeleteConfirm = async () => {
    if (!config.data?.id) return;
    try {
      await deletePayrollBatch({ id: config.data.id }).unwrap();
      toast('success', 'Payroll batch deleted successfully');
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to delete payroll batch'));
    }
  };

  const columns: TableProps<PayrollBatchItem>['columns'] = [
    {
      title: 'Batch Name',
      dataIndex: 'batch_name',
      render: (val: string) => val || 'n/a',
    },
    {
      title: 'Generated On',
      dataIndex: 'created_at',
      align: 'center',
      render: (val: string) => (val ? formatDate(val) : 'n/a'),
    },
    {
      title: 'Type',
      dataIndex: 'pay_basis',
      align: 'center',
      // Batches created before pay types existed are monthly.
      render: (val?: string | null) => (
        <StatusTag value={val || 'MONTHLY'} config={PAY_BASIS_CONFIG} />
      ),
    },
    {
      title: 'Period',
      align: 'center',
      // Falls back to the month/year label for calendar-month batches.
      render: (_, batch) => formatBatchPeriod(batch),
    },
    {
      title: 'Year',
      dataIndex: 'year',
      align: 'center',
      render: (val) => normalizeColumnValue(val),
    },
    {
      title: 'Month',
      dataIndex: 'month',
      align: 'center',
      render: (val) => (val ? dayjs(val).format('MMM') : 'n/a'),
    },
    {
      title: 'Number Of Employee',
      dataIndex: 'total_employees',
      align: 'center',
      render: (val: number) => val ?? 'n/a',
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      render: (val: string) => formatBDT(val || null),
    },
    {
      title: 'Generated',
      align: 'center',
      render: (_, batch) => <GeneratedCount batch={batch} />,
    },
    {
      title: 'Salary Sheet',
      dataIndex: 'salary_sheet',
      render: (val: string, batch) =>
        val === 'GENERATED' ? (
          <button
            type="button"
            onClick={() => navigate(`batch/${batch.id}/salary-sheet`)}
            className="text-theme-primary underline underline-offset-2 decoration-theme-primary p-0"
          >
            Generated
          </button>
        ) : (
          <span className="text-white/40 text-sm">Not Generated</span>
        ),
    },
    {
      title: 'Status',
      align: 'center',
      render: (_, batch) => (
        <Tooltip title={batch.error_response}>
          <div>
            <StatusTag value={batch.status} config={PAYMENT_STATUS_CONFIG} />
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      align: 'end',
      render: (_, batch) => {
        const isFailed = batch.status === 'FAILED';
        const deletable = canDeleteBatch(batch);
        return (
          <Space>
            <Tooltip title={isFailed ? '' : 'Open batch'}>
              <Button
                size="small"
                disabled={isFailed}
                className="action-btn"
                icon={<EyeIcon size={20} />}
                onClick={() => !isFailed && navigate(`batch/${batch.id}`)}
              />
            </Tooltip>
            <Tooltip
              title={
                deletable
                  ? 'Delete batch'
                  : 'Batches with generated salary sheets cannot be deleted'
              }
            >
              <Button
                size="small"
                disabled={!deletable}
                className="action-btn disabled:!opacity-50"
                icon={<TrashIcon size={20} />}
                onClick={() =>
                  deletable && handleOpen({ type: 'DELETE', data: batch })
                }
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <CustomTable<PayrollBatchItem>
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        onPaginate={handlePagination}
        customTitle="Payroll Generated"
        total={data?.data?.meta?.total}
        loading={isLoading || isFetching}
        dataSource={data?.data?.data || []}
        tableRootClassName="!bg-transparent !border-none !p-0"
      />

      <ManagePayroll
        open={config.open && config.type !== 'DELETE'}
        handleClose={handleClose}
      />

      <ConfirmationModal
        loading={deleting}
        wrapperClassName="!p-0"
        handleClose={handleClose}
        handleSubmit={handleDeleteConfirm}
        open={config.open && config.type === 'DELETE'}
        buttonName="Delete"
        title="Delete Payroll Batch"
        subTitle={`Are you sure you want to delete "${config.data?.batch_name}"? This action cannot be undone.`}
      />
    </div>
  );
}
